import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import isEqual from "lodash/isEqual";
import { fitGroupFrame } from "../../vendor/src/features/remotion-editor/editor/items/group/fit-group-frame";
import {isGroupNaturalVoiceUpdate} from './group-voice-natural';
import {isGroupVoiceWindowUpdate} from './group-voice-window';

export type GroupPropertyEdit =
  | "animation"
  | "position"
  | { type: "fit-frame"; groupId: string }
  | { type: "voice-natural"; clipId: string }
  | { type: "voice-window"; clipId: string };

/** Validate property edits instead of interpreting stale group clocks as moves.
 * Position/animation edits keep every interval. An exact explicit frame fit may
 * reset only the target wrapper. A validated natural voice swap may change one
 * audio interval inside unchanged parents; all other items stay unchanged.
 */
export function preservesGroupTimingOnPropertyUpdate(
  previous: EditorState,
  next: EditorState,
  groupPropertyEdit?: GroupPropertyEdit,
): boolean {
  const before = previous.undoableState,
    after = next.undoableState;
  if (
    before.tracks !== after.tracks ||
    before.transitions !== after.transitions
  )
    return false;
  if (before.items === after.items) return true;
  if (Object.keys(before.items).length !== Object.keys(after.items).length)
    return false;
  if (typeof groupPropertyEdit === "object") {
    if(groupPropertyEdit.type==='voice-window')return isGroupVoiceWindowUpdate(previous,next,groupPropertyEdit.clipId);
    if(groupPropertyEdit.type==='voice-natural')return isGroupNaturalVoiceUpdate(previous,next,groupPropertyEdit.clipId);
    if (groupPropertyEdit.type !== "fit-frame") return false;
    const id = groupPropertyEdit.groupId;
    const old = before.items[id],
      item = after.items[id];
    if (
      old?.type !== "group" ||
      item?.type !== "group" ||
      !old.preserveGroupFrame ||
      old.children !== item.children
    )
      return false;
    // Only the explicit fit target may change. Validate against the real fit
    // operation; its interval reset must not rescale members or other groups.
    if (
      Object.entries(before.items).some(
        ([key, value]) => key !== id && after.items[key] !== value,
      )
    )
      return false;
    const expected = fitGroupFrame(before.items, id);
    return expected !== before.items && isEqual(expected[id], item);
  }
  for (const [id, item] of Object.entries(after.items)) {
    const old = before.items[id];
    if (old === item) continue;
    if (
      !old ||
      old.type !== item.type ||
      old.id !== item.id ||
      old.groupId !== item.groupId ||
      old.from !== item.from ||
      old.durationInFrames !== item.durationInFrames
    )
      return false;
    if (old.type === "group" || item.type === "group") {
      if (groupPropertyEdit !== "animation" && groupPropertyEdit !== "position")
        return false;
      const keep = ([key]: [string, unknown]) =>
        groupPropertyEdit === "animation"
          ? key !== "animations" && key !== "positionKeyframes"
          : key !== "left" && key !== "top" && key !== "positionKeyframes";
      const oldProperties = Object.fromEntries(
        Object.entries(old).filter(keep),
      );
      const nextProperties = Object.fromEntries(
        Object.entries(item).filter(keep),
      );
      if (
        [
          ...new Set([
            ...Object.keys(oldProperties),
            ...Object.keys(nextProperties),
          ]),
        ].some((key) => oldProperties[key] !== nextProperties[key])
      )
        return false;
    }
  }
  return true;
}
